/**
 * The top bar per route — the prototype's `HeaderView`, driven by the route
 * registry's `topBar` kind instead of a switch on hash ids.
 *
 * Mount keyed by route id: `useCrumbLabels` calls one hook per dynamic crumb,
 * and a key change is what keeps that count fixed per mount.
 */
import {
  ExportModal,
  TopNavigationBar,
  defaultDatePickerPresets,
  type DatePickerPreset,
  type DateRange,
  type TopNavigationBarComparison,
} from '@deepdive-ui';
import { useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router';

import { sectionLabelOf, navItemById, type AppRoute } from '@/app/router/registry';
import { useDataSource } from '@/features/data/DataSourceContext';
import { fromIsoDate } from '@/lib/analytics/dates';
import { fromPickerRange, toPickerRange } from '@/lib/analytics/periods';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { setCompare, setRange } from '@/store/slices/filtersSlice';

import { useCrumbLabels } from './crumbs';
import { useTopBarActions } from './topBarActions';

export type TopBarContainerProps = { route: AppRoute };

export function TopBarContainer({ route }: TopBarContainerProps) {
  const { filterScope, bootstrap } = useDataSource();
  const params = useParams();
  const filters = useAppSelector((state) => state.filters[filterScope]);
  const dispatch = useAppDispatch();
  const [exportOpen, setExportOpen] = useState(false);

  /* "All time" runs from the first day of data to today (prototype: CORPUS_START…REFERENCE_DATE). */
  const presets = useMemo<DatePickerPreset[]>(() => {
    const today = fromIsoDate(bootstrap.today);
    const start = fromIsoDate(bootstrap.dataStart);
    return defaultDatePickerPresets(today).map((preset) =>
      preset.id === 'all-time' ? { ...preset, getValue: () => ({ start, end: today }) } : preset,
    );
  }, [bootstrap.today, bootstrap.dataStart]);

  const extra = useCrumbLabels(route.crumbs ?? [], params);
  const breadcrumbs = useMemo(() => {
    const nav = navItemById.get(route.navItem);
    const section = sectionLabelOf(route.navItem);
    const trail: { label: string; href?: string }[] = [];
    if (section) trail.push({ label: section });
    if (nav) trail.push({ label: nav.label, href: nav.href });
    let path = nav?.href ?? '';
    (route.crumbs ?? []).forEach((spec, i) => {
      if (spec.kind === 'param') path = `${path}/${encodeURIComponent(params[spec.param] ?? '')}`;
      else path = `${path}/history`;
      trail.push({ label: extra[i] ?? '', href: path });
    });
    const last = trail[trail.length - 1];
    if (last) delete last.href; // the page you are on is not a link
    return trail;
  }, [route, params, extra]);

  const shape = SHAPES[route.topBar];
  const today = fromIsoDate(bootstrap.today);

  const comparison: TopNavigationBarComparison | undefined =
    shape.dates && filters
      ? {
          primary: {
            presets,
            value: toPickerRange(filters.range),
            defaultMonth: filters.range.start ? fromIsoDate(filters.range.start) : today,
            onValueChange: (next: DateRange) =>
              dispatch(setRange({ scope: filterScope, range: fromPickerRange(next) })),
          },
          compare: shape.compare
            ? {
                presets,
                value: filters.compare
                  ? toPickerRange(filters.compare)
                  : { start: null, end: null },
                defaultMonth: filters.compare?.start
                  ? fromIsoDate(filters.compare.start)
                  : filters.range.start
                    ? fromIsoDate(filters.range.start)
                    : today,
                placeholder: 'No comparison',
                onValueChange: (next: DateRange) =>
                  dispatch(
                    setCompare({
                      scope: filterScope,
                      range: next.start || next.end ? fromPickerRange(next) : null,
                    }),
                  ),
              }
            : undefined,
        }
      : undefined;

  const actions: ReactNode = useTopBarActions(route.topBar, () => setExportOpen(true));

  return (
    <>
      <TopNavigationBar
        type="breadcrumb"
        breadcrumbs={breadcrumbs}
        comparison={comparison}
        actions={actions}
      />
      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        description={
          route.filters
            ? "The export covers this page's widgets, over the date range and filters currently applied."
            : "The export covers this page's widgets, over the date range currently applied."
        }
      />
    </>
  );
}

const SHAPES: Record<AppRoute['topBar'], { dates: boolean; compare: boolean }> = {
  module: { dates: true, compare: true },
  'reports-list': { dates: false, compare: false },
  'report-detail': { dates: true, compare: true },
  'deepdive-home': { dates: false, compare: false },
  'deepdive-history': { dates: false, compare: false },
  'deepdive-chat': { dates: false, compare: false },
  settings: { dates: false, compare: false },
  'quick-search': { dates: false, compare: false },
  'influencer-detail': { dates: true, compare: false },
};
